/**
 * Product Master — tenant-wide product catalog admin page.
 *
 * Embedded inside AdminManagement under the "Product Master" tab.
 * Backed by /api/products (CRUD).
 *
 * Lets users:
 *   - List all active and inactive products
 *   - Create a new product (name, code, unit, category, HS/CN code, capacity, site)
 *   - Edit an existing product
 *   - Toggle is_active (soft archive without losing references)
 *
 * Surfaces validation errors (e.g., duplicate code) and shows which sites are
 * available for default_site_id.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Plus, Pencil, X, Check, Search, RefreshCw } from 'lucide-react';
import { useSite } from '../../context/SiteContext';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '',
  code: '',
  description: '',
  category: '',
  unit: 'MT',
  hs_code: '',
  cn_code: '',
  default_site_id: '',
  annual_capacity: '',
  is_active: true,
};

export default function ProductMaster() {
  const { availableSites } = useSite();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, 'new' = create, '<id>' = edit
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = includeInactive ? '?include_inactive=true' : '';
      const r = await axios.get(`${API}/api/products${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(r.data?.products || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const startEdit = (product) => {
    setEditing(product.id);
    setForm({
      name: product.name || '',
      code: product.code || '',
      description: product.description || '',
      category: product.category || '',
      unit: product.unit || 'MT',
      hs_code: product.hs_code || '',
      cn_code: product.cn_code || '',
      default_site_id: product.default_site_id || '',
      annual_capacity: product.annual_capacity ?? '',
      is_active: product.is_active,
    });
  };

  const startCreate = () => {
    setEditing('new');
    setForm(emptyForm);
  };

  const closeEditor = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        description: form.description || null,
        category: form.category || null,
        unit: form.unit || 'MT',
        hs_code: form.hs_code || null,
        cn_code: form.cn_code || null,
        default_site_id: form.default_site_id || null,
        annual_capacity: form.annual_capacity === '' ? null : Number(form.annual_capacity),
        is_active: !!form.is_active,
      };
      if (editing === 'new') {
        await axios.post(`${API}/api/products`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Product created');
      } else {
        await axios.put(`${API}/api/products/${editing}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Product updated');
      }
      closeEditor();
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (product) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/products/${product.id}`,
        { is_active: !product.is_active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(product.is_active ? 'Product archived' : 'Product reactivated');
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update product');
    }
  };

  const filtered = products.filter((p) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      (p.name || '').toLowerCase().includes(f) ||
      (p.code || '').toLowerCase().includes(f) ||
      (p.hs_code || '').toLowerCase().includes(f) ||
      (p.category || '').toLowerCase().includes(f)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Package className="w-6 h-6 text-teal-700" />
          <h2 className="text-xl font-bold text-gray-900">Product Master</h2>
          <span className="text-sm text-gray-500">
            Tenant-wide catalog used by ERS, CBAM, LCA, OIM and BRSR.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search name, code, HS…"
              className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <label className="text-sm flex items-center gap-2 text-gray-700">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Include archived
          </label>
          <button
            onClick={fetchProducts}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
          >
            <Plus className="w-4 h-4" /> New Product
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Unit</th>
                <th className="text-left px-4 py-3">HS / CN</th>
                <th className="text-left px-4 py-3">Default Site</th>
                <th className="text-right px-4 py-3">Capacity</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-gray-500">
                    No products yet. Click <strong>New Product</strong> to create the first one.
                  </td>
                </tr>
              ) : filtered.map((p) => {
                const site = availableSites.find((s) => s.site_id === p.default_site_id);
                return (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{p.name}</td>
                    <td className="px-4 py-3 text-gray-700">{p.code || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{p.category || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{p.unit}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {p.hs_code || '—'}{p.cn_code ? ` / ${p.cn_code}` : ''}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{site?.site_name || (p.default_site_id ? p.default_site_id : '—')}</td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {p.annual_capacity != null
                        ? `${Number(p.annual_capacity).toLocaleString()} ${p.unit}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-green-700 bg-green-50">Active</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-gray-500 bg-gray-100">Archived</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => startEdit(p)}
                        className="p-1 rounded text-teal-700 hover:bg-teal-50"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleActive(p)}
                        className="ml-1 px-2 py-1 text-xs rounded border border-gray-200 text-gray-700 hover:bg-gray-100"
                      >
                        {p.is_active ? 'Archive' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                {editing === 'new' ? 'New Product' : 'Edit Product'}
              </h3>
              <button onClick={closeEditor} className="text-gray-500 hover:text-gray-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name *" required>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </Field>
                <Field label="Code / SKU" hint="Unique per tenant if set">
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="e.g. ETH-99"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </Field>
              </div>
              <Field label="Description">
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Category">
                  <input
                    type="text"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="e.g. Beverages"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </Field>
                <Field label="Unit" hint="Default measurement unit">
                  <input
                    type="text"
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    placeholder="MT, L, m²"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </Field>
                <Field label="Annual capacity">
                  <input
                    type="number"
                    step="any"
                    value={form.annual_capacity}
                    onChange={(e) => setForm({ ...form, annual_capacity: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="HS code" hint="Harmonized System (customs)">
                  <input
                    type="text"
                    value={form.hs_code}
                    onChange={(e) => setForm({ ...form, hs_code: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </Field>
                <Field label="CN code" hint="EU Combined Nomenclature (CBAM)">
                  <input
                    type="text"
                    value={form.cn_code}
                    onChange={(e) => setForm({ ...form, cn_code: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </Field>
              </div>
              <Field label="Default site" hint="Primary manufacturing site (optional)">
                <select
                  value={form.default_site_id}
                  onChange={(e) => setForm({ ...form, default_site_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">— No default site —</option>
                  {availableSites.map((s) => (
                    <option key={s.site_id} value={s.site_id}>{s.site_name || s.site_id}</option>
                  ))}
                </select>
              </Field>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Active (uncheck to archive)
              </label>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 -mx-6 px-6 pt-4 mt-4">
                <button
                  type="button"
                  onClick={closeEditor}
                  className="px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !form.name.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-40"
                >
                  <Check className="w-4 h-4" />
                  {saving ? 'Saving…' : editing === 'new' ? 'Create' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {hint && <span className="text-xs text-gray-400 font-normal ml-2">{hint}</span>}
      </label>
      {children}
    </div>
  );
}
